// help-catalog.ts — CTSK-356. Single source of truth for the help
// page catalog: every shipped `/help/<slug>` doc, with the metadata
// the index page + footer need (slug, title, area, description).
//
// Deliberately METADATA-ONLY: the raw markdown content stays in
// pages/Help.tsx (Vite `?raw` imports), so this module is pure data —
// importable from tests, the router, or future nav without dragging
// the whole docs tree into the bundle graph twice. Help.tsx guards
// slug parity between this catalog and its content map at module
// scope (dev-time throw) and HelpIndex.test.tsx pins it in CI.
//
// Adding a help page (the CTSK-289 pattern):
//   1. Drop the doc at docs/<slug>.md
//   2. Add the `?raw` import + content-map row in pages/Help.tsx
//   3. Add the metadata entry here (pick an existing area if one fits)

export interface HelpPageMeta {
  /** URL fragment: renders at `/help/<slug>`; doc lives at `docs/<slug>.md`. */
  slug: string;
  /** Page title — same string Help.tsx renders as the page h1. */
  title: string;
  /** Grouping key for the /help index (Tools / Bundles / Sessions / …). */
  area: string;
  /** One-line summary shown under the title on the /help index. */
  description: string;
  /** Repo path of the backing doc when it is NOT `docs/<slug>.md`
   *  (e.g. an ADR under docs/decisions/). Display-only. */
  sourcePath?: string;
}

/**
 * All shipped help pages, in index display order. Areas group in
 * first-appearance order (Tools → Bundles → Sessions → PATs → Nests
 * → API).
 */
export const HELP_PAGES: readonly HelpPageMeta[] = [
  // CTSK-289 — linked from Settings → Tools panel header.
  {
    slug: 'tools',
    title: 'Tools — install / configure / round-trip',
    area: 'Tools',
    description:
      'Install external tools, configure manifests + actions, and round-trip settings.',
  },
  // CTSK-381 — linked from DataSourcesRegistry tool page `?` icon.
  {
    slug: 'data-sources-registry',
    title: 'Data Sources Registry — file-based tool + bookmark registry',
    area: 'Tools',
    description:
      'What DSR is, file layout, ingest paths (clipper + direct edit), soft-delete semantics, bookmarks vs. sources.',
  },
  // CTSK-382 — linked from TimelineLane tool page `?` icon.
  {
    slug: 'timeline-lane',
    title: 'Timeline Lane — dataset-driven timeline viz',
    area: 'Tools',
    description:
      'Dataset selection, events.yaml format, BCE date handling, iframe-wrapper viz over registry files.',
  },
  // CTSK-400 — bundle-installer/tool-author deep reference, linked
  // from the /help/tools "Tool onboarding checklist" section.
  {
    slug: 'tool-onboarding-standard',
    title: 'Tool Onboarding Standard',
    area: 'Tools',
    description:
      'Deep-reference standard for adding a new tool to Captain Tasko — the 10-item minimum bar with file paths, code shapes, and patterns to copy.',
  },
  // CTSK-375..380 — Settings sub-page docs (EPIC CTSK-374), linked
  // from each Settings tab's page-header `?` icon (CTSK-355 sweep).
  {
    slug: 'settings-general',
    title: 'General — defaults, appearance, save/discard',
    area: 'Settings',
    description:
      'App-wide presentation defaults, the envelope save/discard flow, and the per-org theme color.',
  },
  {
    slug: 'settings-documents',
    title: 'Documents — viewer & bulk-edit defaults',
    area: 'Settings',
    description:
      'PDF viewer, default zoom, inbox-tag handling, search scope, bulk-edit behavior, notes, and grouping.',
  },
  // CTSK-523 — Search split out of Documents; Data Pipeline reserved.
  {
    slug: 'settings-search',
    title: 'Search — ranking, facets, telemetry & index health',
    area: 'Settings',
    description:
      'Rank weights, label weight, facet counts, query logging, the grouping gate, and the search health card.',
  },
  {
    slug: 'settings-data-pipeline',
    title: 'Data Pipeline — reserved for a future release',
    area: 'Settings',
    description:
      'Placeholder tab reserving the /settings/data-pipeline address for a future pipeline configuration surface.',
  },
  {
    slug: 'settings-users',
    title: 'Users — accounts, passwords, enable/disable',
    area: 'Settings',
    description:
      'Create accounts with one-time passwords, enable/disable, admin resets, and where roles live.',
  },
  {
    slug: 'settings-groups',
    title: 'Groups — team rosters & membership',
    area: 'Settings',
    description:
      'Group vs role vs org, managing rosters, and the member typeahead.',
  },
  {
    slug: 'settings-organizations',
    title: 'Organizations — multi-org model & lifecycle',
    area: 'Settings',
    description:
      'The multi-org model, active-org picker, lifecycle ladder, members, and PAT org-locking.',
  },
  {
    slug: 'settings-tool-palette',
    title: 'Tool Palette — curating the document action strip',
    area: 'Settings',
    description:
      'Per-org button visibility + ordering for the document action strip, and the icon allowlist.',
  },
  // CTSK-286 — linked from Settings → Bundles panel header.
  {
    slug: 'bundles',
    title: 'Bundles — export / import org config',
    area: 'Bundles',
    description: 'Export and import org configuration as versioned bundles.',
  },
  // CTSK-281 — linked from the "Sessions" header on Project Detail.
  {
    slug: 'project-sessions',
    title: 'Project sessions — refs, kinds, and how to add them',
    area: 'Sessions',
    description:
      'Session refs on projects: the four kinds, registration, and the Sessions section.',
  },
  // CTSK-TBD (child of CTSK-166) — linked from the External Clients
  // onboarding card's "Finding the session UID & tail" strip. Routes the
  // existing ADR; doc lives at docs/decisions/0003-session-identity-hardening.md.
  {
    slug: 'session-identity-hardening',
    title: 'ADR 0003 — Session-identity + bootstrap-PAT hardening',
    area: 'Sessions',
    description:
      'How sub_uuid_tail_first8 is derived from local_/cse_ session ids, and the five bootstrap-PAT invariants.',
    sourcePath: 'docs/decisions/0003-session-identity-hardening.md',
  },
  {
    slug: 'tasko-mcp-setup',
    title: 'tasko-mcp — first-time setup',
    area: 'Sessions',
    description: 'First-time setup for the tasko-mcp client, PAT env files included.',
  },
  {
    slug: 'pats-api',
    title: 'Personal Access Tokens — API reference',
    area: 'PATs',
    description: 'PAT lifecycle, scopes, and the full PATs API reference.',
  },
  // CTSK-250 — linked from PatsPanel's "Learn more about roles →".
  {
    slug: 'role-catalog',
    title: 'Role catalog reference',
    area: 'PATs',
    description: 'Named role handles and the capability lists they expand to.',
  },
  // CTSK-383 — linked from the External Clients nest page's `?` icon.
  {
    slug: 'external-clients-nest',
    title: 'External Clients Nest — operator walkthrough',
    area: 'Nests',
    description:
      'The operator flow: approve + mint, deny, change role, revoke, audit trail, and common 422s.',
  },
  {
    slug: 'nests-auth-matrix',
    title: 'Nests + External Clients Nest — Auth Model Matrix',
    area: 'Nests',
    description: 'Who can do what: the auth model matrix for nests and the ECN.',
  },
  {
    slug: 'external-clients-role-change-flow',
    title: 'External Clients Nest — Role change flow (canonical vs. fast path)',
    area: 'Nests',
    description: 'How external-client role changes flow — canonical path vs. fast path.',
  },
  {
    slug: 'api-howto',
    title: 'API — curl recipes',
    area: 'API',
    description: 'Practical curl recipes for common REST API tasks.',
  },
  {
    slug: 'api-contract',
    title: 'API contract reference',
    area: 'API',
    description: 'Envelopes, error shapes, and the endpoint contract reference.',
  },
  {
    slug: 'documents-ingest-api',
    title: 'Document ingest API',
    area: 'API',
    description: 'Ingesting documents via the API: endpoints, payloads, and states.',
  },
  // CTSK-389 — linked from Settings → Audit panel header.
  {
    slug: 'audit',
    title: 'Audit — reading the resource history trail',
    area: 'Audit',
    description: 'What resource_audit captures, how to read the timeline, drill-down, and access.',
  },
  // CTSK-333 (Phase 3 of EPIC CTSK-385) — linked from Settings →
  // Recycle Bin panel header.
  {
    slug: 'recycle-bin',
    title: 'Recycle Bin — restore or purge soft-deleted rows',
    area: 'Admin',
    description: 'What the recycle bin is, restore vs. purge semantics, per-table policy, and cap requirements.',
  },
];
